import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { UserAvatar } from '@/components/UserAvatar';
import {
  Shield, Coins, Euro, Video, Zap, Loader2, Plus, Minus, Activity, Mail, Trash2, Save,
  KeyRound, Ban, ShieldCheck, LogIn, ArrowLeft, StickyNote, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { StatCard, PlanBadge, EngagementBadge, AnalysisStatusBadge, useAdminFmt } from '@/components/AdminShared';

const Card = ({ title, icon: Icon, iconColor = '#00B4D8', children, testid, danger = false }) => (
  <div className={`kite-card p-5 ${danger ? 'border-[#FF6B35]/30' : ''}`} data-testid={testid}>
    {title && (
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4" style={{ color: danger ? '#FF6B35' : iconColor }} />}
        <span className={`label-eyebrow ${danger ? 'text-[#FF6B35]' : 'text-muted-foreground'}`}>{title}</span>
      </div>
    )}
    {children}
  </div>
);

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { fmtDate, fmtDateTime } = useAdminFmt();

  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editForm, setEditForm] = useState(null);
  const [notesForm, setNotesForm] = useState({ notes: '', tags: '' });
  const [creditDelta, setCreditDelta] = useState('');
  const [creditSet, setCreditSet] = useState('');
  const [resetPw, setResetPw] = useState('');
  const [busy, setBusy] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userAnalyses, setUserAnalyses] = useState([]);
  const [loadingUserAnalyses, setLoadingUserAnalyses] = useState(false);

  const syncForms = (data) => {
    const u = data?.user;
    if (u) setEditForm({ name: u.name || '', email: u.email || '', skill_level: u.skill_level || 'beginner', units: u.units || 'metric', weight_kg: u.weight_kg ?? '' });
    setNotesForm({ notes: data?.admin_notes || '', tags: (data?.admin_tags || []).join(', ') });
  };

  const loadDetail = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setSelected(data); syncForms(data);
    } catch (e) { toast.error(apiError(e, t('admin.loadUserError'))); }
    finally { setLoading(false); }
  };

  const loadUserAnalyses = async () => {
    setLoadingUserAnalyses(true);
    try { const { data } = await api.get(`/admin/users/${id}/analyses`); setUserAnalyses(data); }
    catch { setUserAnalyses([]); }
    finally { setLoadingUserAnalyses(false); }
  };

  useEffect(() => {
    const init = async () => { await Promise.all([loadDetail(), loadUserAnalyses()]); };
    init();
  }, [id]);

  const refreshDetail = async (reSync = false) => {
    try { const { data } = await api.get(`/admin/users/${id}`); setSelected(data); if (reSync) syncForms(data); else setNotesForm({ notes: data?.admin_notes || '', tags: (data?.admin_tags || []).join(', ') }); } catch (e) { console.error('refreshDetail failed', e); }
  };

  const adjustCredits = async (payload, key) => {
    setBusy(key);
    try {
      const { data } = await api.post(`/admin/users/${id}/credits`, payload);
      toast.success(t('admin.creditsUpdated', { credits: data.credits }));
      setCreditDelta(''); setCreditSet('');
      await refreshDetail();
    } catch (e) { toast.error(apiError(e, t('admin.creditsError'))); } finally { setBusy(''); }
  };

  const changePlan = async (tier, interval) => {
    setBusy(`plan-${tier}-${interval || ''}`);
    try {
      await api.post(`/admin/users/${id}/plan`, { tier, interval });
      toast.success(tier === 'elite' ? t('admin.upgradedElite') : tier === 'pro' ? t('admin.upgradedPro') : t('admin.downgradedFree'));
      await refreshDetail();
    } catch (e) { toast.error(apiError(e, t('admin.planError'))); } finally { setBusy(''); }
  };

  const saveEdit = async () => {
    if (!editForm) return;
    setSavingEdit(true);
    try {
      await api.put(`/admin/users/${id}`, {
        name: editForm.name.trim(), email: editForm.email.trim(), skill_level: editForm.skill_level,
        units: editForm.units, weight_kg: editForm.weight_kg === '' ? null : Number(editForm.weight_kg),
      });
      toast.success(t('admin.profileUpdated'));
      await refreshDetail(true);
    } catch (e) { toast.error(apiError(e, t('admin.profileError'))); } finally { setSavingEdit(false); }
  };

  const resetPassword = async () => {
    if (resetPw.length < 6) { toast.error(t('admin.passwordTooShort')); return; }
    setBusy('reset-pw');
    try {
      await api.post(`/admin/users/${id}/reset-password`, { password: resetPw });
      toast.success(t('admin.passwordReset')); setResetPw('');
    } catch (e) { toast.error(apiError(e, t('admin.resetPasswordError'))); } finally { setBusy(''); }
  };

  const toggleSuspend = async (suspend) => {
    setBusy('suspend');
    try {
      await api.post(`/admin/users/${id}/suspend`, { suspended: suspend });
      toast.success(suspend ? t('admin.userSuspended') : t('admin.userReactivated'));
      await refreshDetail();
    } catch (e) { toast.error(apiError(e, t('admin.suspendError'))); } finally { setBusy(''); }
  };

  const saveNotes = async () => {
    setBusy('notes');
    try {
      const tags = notesForm.tags.split(',').map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put(`/admin/users/${id}/notes`, { notes: notesForm.notes, tags });
      setNotesForm({ notes: data.admin_notes || '', tags: (data.admin_tags || []).join(', ') });
      toast.success(t('admin.notesSaved'));
    } catch (e) { toast.error(apiError(e, t('admin.notesError'))); } finally { setBusy(''); }
  };

  const impersonate = async () => {
    setBusy('impersonate');
    try {
      const { data } = await api.post(`/admin/users/${id}/impersonate`);
      // Stash the admin's own tokens so the banner can restore them on exit.
      const adminToken = localStorage.getItem('kiteiq_token');
      const adminRefresh = localStorage.getItem('kiteiq_refresh');
      if (adminToken) localStorage.setItem('kiteiq_admin_token', adminToken);
      if (adminRefresh) localStorage.setItem('kiteiq_admin_refresh', adminRefresh);
      localStorage.setItem('kiteiq_imp_name', data.user?.name || data.user?.email || 'user');
      localStorage.setItem('kiteiq_token', data.token);
      // Impersonation sessions have NO refresh token — they end when the short
      // access token expires or when the admin clicks "Exit". This also stops
      // the refresh interceptor from silently upgrading back to the admin.
      localStorage.removeItem('kiteiq_refresh');
      window.location.href = '/dashboard';
    } catch (e) { toast.error(apiError(e, t('admin.impersonateError'))); setBusy(''); }
  };

  const toggleAdmin = async (value) => {
    setBusy('admin');
    try {
      await api.put(`/admin/users/${id}`, { is_admin: value });
      toast.success(value ? t('admin.adminGranted') : t('admin.adminRevoked'));
      await refreshDetail(true);
    } catch (e) { toast.error(apiError(e, t('admin.adminError'))); } finally { setBusy(''); }
  };

  const deleteAnalysis = async (analysisId) => {
    setBusy(`del-${analysisId}`);
    try {
      await api.delete(`/admin/analyses/${analysisId}`);
      toast.success(t('admin.analysisDeleted'));
      await loadUserAnalyses();
    } catch (e) { toast.error(apiError(e, t('admin.analysisDeleteError'))); } finally { setBusy(''); }
  };

  const deleteUser = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success(t('admin.userDeleted'));
      navigate('/admin');
    } catch (e) { toast.error(apiError(e, t('admin.deleteUserError'))); setDeleting(false); }
  };

  const row = selected?.row;

  return (
    <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <Button variant="ghost" onClick={() => navigate('/admin')} className="text-muted-foreground hover:text-white -ml-2 mb-4" data-testid="admin-back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> {t('admin.backToUsers')}
      </Button>

      {loading || !selected ? (
        <div className="space-y-6">
          <Skeleton className="h-24 bg-white/10 rounded-2xl" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Skeleton className="lg:col-span-4 h-96 bg-white/10 rounded-2xl" />
            <Skeleton className="lg:col-span-8 h-96 bg-white/10 rounded-2xl" />
          </div>
        </div>
      ) : (
        <div data-testid="admin-user-detail-page">
          {/* Header */}
          <div className="kite-card p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="flex items-center gap-4 min-w-0">
              <UserAvatar src={selected.user?.avatar} name={row?.name || row?.email} size={56} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-display text-2xl sm:text-3xl truncate">{row?.name || t('profile.rider')}</h1>
                  <PlanBadge tier={row?.tier} />
                  {row?.is_admin && <Badge className="bg-[#FFB020]/15 text-[#FFB020] border border-[#FFB020]/30"><Shield className="w-3 h-3 mr-1" /> {t('admin.adminBadge')}</Badge>}
                  {selected.suspended
                    ? <Badge variant="outline" className="bg-[#FF6B35]/15 text-[#FF6B35] border-[#FF6B35]/30"><Ban className="w-3 h-3 mr-1" />{t('admin.suspendedBadge')}</Badge>
                    : <EngagementBadge status={selected.activity?.engagement_status} t={t} />}
                </div>
                <div className="text-muted-foreground flex items-center gap-1.5 mt-1 text-sm"><Mail className="w-3.5 h-3.5" /> {row?.email}</div>
              </div>
            </div>
            {!row?.is_admin && (
              <Button onClick={impersonate} disabled={busy === 'impersonate' || selected.suspended} className="bg-[#48CAE4] hover:bg-[#00B4D8] text-black font-semibold rounded-xl shrink-0" data-testid="admin-impersonate-btn">
                {busy === 'impersonate' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4 mr-2" /> {t('admin.impersonate')}</>}
              </Button>
            )}
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-6">
            <StatCard icon={Zap} label={t('admin.colCredits')} value={row?.credits ?? 0} accent="#00B4D8" testid="admin-detail-credits" />
            <StatCard icon={LogIn} label={t('admin.loginCount')} value={selected.activity?.login_count ?? 0} accent="#48CAE4" testid="admin-detail-login-count" />
            <StatCard icon={Video} label={t('admin.stats.analyses')} value={selected.stats?.analyses ?? 0} accent="#2EE59D" />
            <StatCard icon={Euro} label={t('profile.billingHistory')} value={(selected.payments || []).length} accent="#FFB020" />
            <StatCard icon={Activity} label={t('admin.engagementLabel')} value={t(`admin.engagement.${selected.activity?.engagement_status || 'new'}`)} accent="#FF6B35" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
            {/* LEFT COLUMN */}
            <div className="lg:col-span-4 space-y-6">
              {/* Edit profile */}
              {editForm && (
                <Card title={t('admin.editProfile')} icon={Save} testid="admin-edit-card">
                  <div className="space-y-2.5">
                    <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder={t('admin.name')} className="bg-[#12161B] border-white/15" data-testid="admin-edit-name" />
                    <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder={t('admin.email')} className="bg-[#12161B] border-white/15" data-testid="admin-edit-email" />
                    <div className="grid grid-cols-2 gap-2.5">
                      <Select value={editForm.skill_level} onValueChange={(v) => setEditForm({ ...editForm, skill_level: v })}>
                        <SelectTrigger className="bg-[#12161B] border-white/15" data-testid="admin-edit-skill"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">{t('levels.beginner')}</SelectItem>
                          <SelectItem value="intermediate">{t('levels.intermediate')}</SelectItem>
                          <SelectItem value="advanced">{t('levels.advanced')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={editForm.units} onValueChange={(v) => setEditForm({ ...editForm, units: v })}>
                        <SelectTrigger className="bg-[#12161B] border-white/15" data-testid="admin-edit-units"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="metric">{t('admin.metricUnits')}</SelectItem>
                          <SelectItem value="imperial">{t('admin.imperialUnits')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Input type="number" value={editForm.weight_kg} onChange={(e) => setEditForm({ ...editForm, weight_kg: e.target.value })} placeholder={t('admin.weightKg')} className="bg-[#12161B] border-white/15" data-testid="admin-edit-weight" />
                    <Button size="sm" onClick={saveEdit} disabled={savingEdit} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-lg w-full" data-testid="admin-edit-save">
                      {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> {t('admin.saveProfile')}</>}
                    </Button>
                  </div>
                </Card>
              )}

              {/* Reset password */}
              <Card title={t('admin.resetPassword')} icon={KeyRound} iconColor="#48CAE4" testid="admin-reset-card">
                <div className="flex items-center gap-2">
                  <Input type="text" autoComplete="new-password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder={t('admin.newPasswordPlaceholder')} className="bg-[#12161B] border-white/15" data-testid="admin-reset-pw-input" />
                  <Button size="sm" disabled={resetPw.length < 6 || busy === 'reset-pw'} onClick={resetPassword} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-lg shrink-0" data-testid="admin-reset-pw-btn">
                    {busy === 'reset-pw' ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.resetPasswordBtn')}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{t('admin.resetPasswordHint')}</p>
              </Card>

              {/* Account actions */}
              <Card title={t('admin.accountActions')} icon={ShieldCheck} iconColor="#48CAE4" testid="admin-account-card">
                <div className="flex items-center justify-between rounded-xl bg-[#12161B] border border-white/10 px-3 py-2.5 mb-3">
                  <div className="flex items-center gap-2 text-sm"><Shield className="w-4 h-4 text-[#FFB020]" /> {t('admin.adminAccess')}</div>
                  <Switch checked={!!row?.is_admin} disabled={busy === 'admin'} onCheckedChange={toggleAdmin} data-testid="admin-toggle-admin" />
                </div>
                {!row?.is_admin && (
                  selected.suspended ? (
                    <Button size="sm" disabled={busy === 'suspend'} onClick={() => toggleSuspend(false)} className="w-full bg-[#2EE59D] hover:bg-[#2EE59D]/80 text-black font-semibold rounded-lg" data-testid="admin-unsuspend-btn">
                      {busy === 'suspend' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-2" /> {t('admin.reactivate')}</>}
                    </Button>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="w-full border-[#FF6B35]/40 bg-[#FF6B35]/5 text-[#FF6B35] hover:bg-[#FF6B35]/15 rounded-lg" data-testid="admin-suspend-btn"><Ban className="w-4 h-4 mr-2" /> {t('admin.suspend')}</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#12161B] border-white/15 text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('admin.suspendTitle')}</AlertDialogTitle>
                          <AlertDialogDescription className="text-muted-foreground">{t('admin.suspendDesc')}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-transparent border-white/15 hover:bg-white/10">{t('admin.cancel')}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => toggleSuspend(true)} className="bg-[#FF6B35] hover:bg-[#FFB020] text-black" data-testid="admin-suspend-confirm">{t('admin.suspendConfirm')}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )
                )}
              </Card>

              {/* Internal notes & tags */}
              <Card title={t('admin.notesTitle')} icon={StickyNote} iconColor="#FFB020" testid="admin-notes-card">
                <Textarea value={notesForm.notes} onChange={(e) => setNotesForm({ ...notesForm, notes: e.target.value })} placeholder={t('admin.notesPlaceholder')} className="bg-[#12161B] border-white/15 min-h-[80px]" data-testid="admin-notes-input" />
                <Input value={notesForm.tags} onChange={(e) => setNotesForm({ ...notesForm, tags: e.target.value })} placeholder={t('admin.tagsPlaceholder')} className="mt-2 bg-[#12161B] border-white/15" data-testid="admin-tags-input" />
                <Button size="sm" disabled={busy === 'notes'} onClick={saveNotes} className="mt-2 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-lg w-full" data-testid="admin-notes-save">
                  {busy === 'notes' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> {t('admin.saveNotes')}</>}
                </Button>
              </Card>

              {/* Danger zone */}
              <Card title={t('admin.dangerZone')} danger testid="admin-danger-card">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" disabled={row?.is_admin} className="w-full border-[#FF6B35]/40 bg-[#FF6B35]/5 text-[#FF6B35] hover:bg-[#FF6B35]/15 rounded-xl disabled:opacity-50" data-testid="admin-delete-user-btn">
                      <Trash2 className="w-4 h-4 mr-2" /> {t('admin.deleteUser')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#12161B] border-white/15 text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('admin.deleteUserTitle', { name: row?.name || t('admin.deleteUserFallback') })}</AlertDialogTitle>
                      <AlertDialogDescription className="text-muted-foreground">{t('admin.deleteUserDesc')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-white/15 hover:bg-white/10">{t('admin.cancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={deleteUser} disabled={deleting} className="bg-[#FF6B35] hover:bg-[#FFB020] text-black" data-testid="admin-delete-confirm">
                        {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.deletePermanently')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {row?.is_admin && <p className="text-xs text-muted-foreground mt-2">{t('admin.adminCantDelete')}</p>}
              </Card>
            </div>

            {/* RIGHT COLUMN */}
            <div className="lg:col-span-8 space-y-6">
              {/* Activity & engagement */}
              <Card title={t('admin.activityTitle')} icon={Activity} iconColor="#2EE59D" testid="admin-activity-card">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="rounded-xl bg-[#12161B] border border-white/10 p-3">
                    <div className="text-xs text-muted-foreground">{t('admin.lastLogin')}</div>
                    <div className="font-medium mt-1" data-testid="admin-detail-last-login-val">{fmtDateTime(selected.activity?.last_login_at)}</div>
                  </div>
                  <div className="rounded-xl bg-[#12161B] border border-white/10 p-3">
                    <div className="text-xs text-muted-foreground">{t('admin.lastActive')}</div>
                    <div className="font-medium mt-1">{fmtDateTime(selected.activity?.last_active_at)}</div>
                  </div>
                  <div className="rounded-xl bg-[#12161B] border border-white/10 p-3">
                    <div className="text-xs text-muted-foreground">{t('admin.colJoined')}</div>
                    <div className="font-medium mt-1">{fmtDate(row?.created_at)}</div>
                  </div>
                  <div className="rounded-xl bg-[#12161B] border border-white/10 p-3 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-[#FC4C02]" /> {row?.strava_connected ? t('admin.stravaOn') : t('admin.noStrava')}
                  </div>
                </div>
              </Card>

              {/* Credits & plan controls */}
              <Card title={t('admin.changePlan')} icon={Coins} iconColor="#FFB020" testid="admin-plan-card">
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" disabled={busy.startsWith('plan')} onClick={() => changePlan('pro', 'pro_monthly')} className="bg-[#FF6B35] hover:bg-[#FFB020] text-black font-semibold rounded-lg" data-testid="admin-plan-pro-monthly">{t('admin.proMonthly')}</Button>
                  <Button size="sm" disabled={busy.startsWith('plan')} onClick={() => changePlan('pro', 'pro_yearly')} className="bg-[#FF6B35] hover:bg-[#FFB020] text-black font-semibold rounded-lg" data-testid="admin-plan-pro-yearly">{t('admin.proYearly')}</Button>
                  <Button size="sm" disabled={busy.startsWith('plan')} onClick={() => changePlan('elite', 'elite_monthly')} className="bg-[#FFB020] hover:bg-[#ffc04d] text-black font-semibold rounded-lg" data-testid="admin-plan-elite-monthly">{t('admin.eliteMonthly')}</Button>
                  <Button size="sm" disabled={busy.startsWith('plan')} onClick={() => changePlan('elite', 'elite_yearly')} className="bg-[#FFB020] hover:bg-[#ffc04d] text-black font-semibold rounded-lg" data-testid="admin-plan-elite-yearly">{t('admin.eliteYearly')}</Button>
                  <Button size="sm" variant="outline" disabled={busy.startsWith('plan')} onClick={() => changePlan('free')} className="border-white/15 bg-white/5 hover:bg-white/10 rounded-lg" data-testid="admin-plan-free">{t('admin.setFree')}</Button>
                </div>
                <div className="border-t border-white/10 mt-4 pt-4">
                  <Label className="text-sm">{t('admin.adjustCredits')}</Label>
                  <div className="flex flex-col sm:flex-row gap-3 mt-2">
                    <div className="flex items-center gap-2 flex-1">
                      <Input type="number" value={creditDelta} onChange={(e) => setCreditDelta(e.target.value)} placeholder={t('admin.deltaPlaceholder')} className="bg-[#12161B] border-white/15" data-testid="admin-credit-delta" />
                      <Button size="icon" disabled={!creditDelta || busy === 'add'} onClick={() => adjustCredits({ delta: Math.abs(parseInt(creditDelta, 10)) }, 'add')} className="bg-[#2EE59D] hover:bg-[#2EE59D]/80 text-black rounded-lg shrink-0" title={t('admin.addCredits')} data-testid="admin-credit-add">{busy === 'add' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}</Button>
                      <Button size="icon" disabled={!creditDelta || busy === 'sub'} onClick={() => adjustCredits({ delta: -Math.abs(parseInt(creditDelta, 10)) }, 'sub')} className="bg-[#FF6B35] hover:bg-[#FFB020] text-black rounded-lg shrink-0" title={t('admin.removeCredits')} data-testid="admin-credit-sub">{busy === 'sub' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Minus className="w-4 h-4" />}</Button>
                    </div>
                    <div className="flex items-center gap-2 flex-1">
                      <Input type="number" value={creditSet} onChange={(e) => setCreditSet(e.target.value)} placeholder={t('admin.setExact')} className="bg-[#12161B] border-white/15" data-testid="admin-credit-set-input" />
                      <Button size="sm" disabled={creditSet === '' || busy === 'set'} onClick={() => adjustCredits({ set_to: parseInt(creditSet, 10) }, 'set')} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-lg shrink-0" data-testid="admin-credit-set-btn">{busy === 'set' ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.set')}</Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{t('admin.planNote')}</p>
                </div>
              </Card>

              {/* Sessions & videos */}
              <Card title={`${t('admin.sessionsTitle')} (${userAnalyses.length})`} icon={Video} iconColor="#48CAE4" testid="admin-sessions-card">
                <div className="space-y-2" data-testid="admin-sessions-list">
                  {loadingUserAnalyses ? (
                    <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#00B4D8]" /></div>
                  ) : userAnalyses.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">{t('admin.noSessions')}</p>
                  ) : userAnalyses.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 rounded-xl bg-[#12161B] border border-white/10 p-2.5" data-testid="admin-session-item">
                      <div className="w-16 h-11 rounded-md bg-black/40 overflow-hidden shrink-0 flex items-center justify-center">
                        {a.thumbnail ? <img src={a.thumbnail} alt="" className="w-full h-full object-cover" /> : <Video className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <AnalysisStatusBadge status={a.status} t={t} />
                          {a.overall_score != null && <span className="text-xs font-data text-[#00B4D8]">{a.overall_score}</span>}
                          {a.is_reference && <Badge variant="outline" className="border-[#FFB020]/40 text-[#FFB020] text-[10px]">REF</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate mt-0.5">{a.spot_name || t('admin.noSpot')} · {fmtDateTime(a.created_at)}</div>
                        {a.status === 'failed' && a.error && <div className="text-xs text-[#FF6B35] truncate mt-0.5" title={a.error}>{a.error}</div>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <a href={`/analysis/${a.id}`} target="_blank" rel="noreferrer" className="p-1.5 rounded-md hover:bg-white/10 text-muted-foreground hover:text-white" title={t('admin.openAnalysis')} data-testid="admin-session-open"><ExternalLink className="w-4 h-4" /></a>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="p-1.5 rounded-md hover:bg-[#FF6B35]/15 text-muted-foreground hover:text-[#FF6B35]" title={t('admin.deleteAnalysis')} data-testid="admin-session-delete">
                              {busy === `del-${a.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#12161B] border-white/15 text-white">
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t('admin.deleteAnalysisTitle')}</AlertDialogTitle>
                              <AlertDialogDescription className="text-muted-foreground">{t('admin.deleteAnalysisDesc')}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-transparent border-white/15 hover:bg-white/10">{t('admin.cancel')}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteAnalysis(a.id)} className="bg-[#FF6B35] hover:bg-[#FFB020] text-black">{t('admin.deletePermanently')}</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Billing history */}
              <Card title={t('profile.billingHistory')} icon={Euro} iconColor="#2EE59D" testid="admin-billing-card">
                {(selected.payments || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('admin.noPaymentsTable')}</p>
                ) : (
                  <div className="divide-y divide-white/8">
                    {selected.payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between py-2.5 text-sm">
                        <div>
                          <div className="font-medium">{p.description}{p.demo && <span className="text-xs text-muted-foreground"> · {t('profile.test')}</span>}</div>
                          <div className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</div>
                        </div>
                        <span className="font-data font-semibold">€{Number(p.amount_eur || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
